import { forwardRef } from 'react';
import { Align, Justify, Direction, SpaceVariant } from '../Common/types';
import { StyledBox } from '../Common/StyledBox';
import {
  CommonBoxProps,
  resolveCommonBoxProps,
  resolveResponsiveProp,
  ResponsiveProp,
  spaceVariantToValue,
} from '../Common/boxUtils';

export type FlexBoxProps = CommonBoxProps & {
  justify?: ResponsiveProp<Justify>;
  align?: ResponsiveProp<Align>;
  gap?: ResponsiveProp<SpaceVariant>;
  flexWrap?: boolean;
  flex?: ResponsiveProp<number>;
  flexDirection?: ResponsiveProp<Direction>;
};

const InternalFlexBox = (
  {
    children,
    padding,
    paddingX,
    paddingY,
    paddingLeft,
    paddingRight,
    paddingBottom,
    paddingTop,
    margin,
    marginX,
    marginY,
    marginLeft,
    marginRight,
    marginBottom,
    marginTop,
    borderWidth,
    borderWidthLeft,
    borderWidthRight,
    borderWidthTop,
    borderWidthBottom,
    borderColor,
    isolate,
    position,
    justify = 'flex-start', // default value
    align = 'stretch', // default value
    gap = 'none', // default value
    flexWrap = false,
    flexDirection,
    flex,
    width,
    height,
    backgroundColor,
    boxShadow,
    borderRadius,
    flexGrow,
    flexShrink,
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,
    overflow,
    overflowX,
    overflowY,
    zIndex,
    transition,
    ...rest
  }: FlexBoxProps,
  ref: React.ForwardedRef<HTMLElement>
) => {
  const resolvedGap = spaceVariantToValue(gap);

  const commonBoxProps = resolveCommonBoxProps({
    padding,
    paddingX,
    paddingY,
    paddingLeft,
    paddingRight,
    paddingBottom,
    paddingTop,
    margin,
    marginX,
    marginY,
    marginLeft,
    marginRight,
    marginBottom,
    marginTop,
    borderWidth,
    borderWidthLeft,
    borderWidthRight,
    borderWidthTop,
    borderWidthBottom,
    borderColor,
    isolate,
    position,
    width,
    height,
    backgroundColor,
    boxShadow,
    borderRadius,
    flexGrow,
    flexShrink,
    minWidth,
    maxWidth,
    minHeight,
    maxHeight,
    overflow,
    overflowX,
    overflowY,
    zIndex,
    transition,
  });

  return (
    <StyledBox
      {...commonBoxProps}
      $display={'flex'}
      $justify={resolveResponsiveProp<Justify, Justify>(justify)}
      $align={resolveResponsiveProp<Align, Align>(align)}
      $gap={resolvedGap}
      $flexWrap={flexWrap}
      $flex={resolveResponsiveProp(flex?.toString())}
      $flexDirection={resolveResponsiveProp<Direction, Direction>(
        flexDirection
      )}
      {...rest}
      ref={ref}
    >
      {children}
    </StyledBox>
  );
};

export const FlexBox = forwardRef(InternalFlexBox);
